import { Helmet } from "react-helmet";
import meta from "./meta.json";
import {
  DEFAULT_OG_IMAGE,
  SITE_LOCALE,
  SITE_NAME,
  buildBreadcrumbListStructuredData,
  buildLocalBusinessStructuredData,
  buildOrganizationStructuredData,
  buildWebPageStructuredData,
  buildWebSiteStructuredData,
  normalizePath,
  toAbsoluteUrl,
} from "./structuredData";

const getMeta = (path) => meta[path] || {};
const NOINDEX_PATHS = new Set([
  "/admin-panel",
  "/admin-panel/:sessionId",
  "/login/blog",
  "/edit/:id",
  "/upss",
]);

export default function PageMeta({
  path,
  fallbackTitle,
  fallbackDescription,
  structuredData,
  canonicalPath,
  ogType = "website",
  ogImage = DEFAULT_OG_IMAGE,
  noindex,
  breadcrumbTitle,
}) {
  const metaEntry = getMeta(path);
  const defaultTitle = meta["/"]?.title || "eGodziny";
  const title = fallbackTitle || metaEntry.title || defaultTitle;
  const description =
    typeof fallbackDescription === "string"
      ? fallbackDescription
      : metaEntry.description;
  const normalizedPath = normalizePath(canonicalPath || path || "/");
  const canonicalUrl = toAbsoluteUrl(normalizedPath);
  const resolvedNoindex =
    typeof noindex === "boolean" ? noindex : NOINDEX_PATHS.has(path);
  const robotsContent = resolvedNoindex ? "noindex, nofollow" : "index, follow";
  const shouldExposeCanonical = !resolvedNoindex;

  const pageStructuredData = Array.isArray(structuredData)
    ? structuredData.filter(Boolean)
    : structuredData
      ? [structuredData]
      : [];

  const defaultStructuredData = resolvedNoindex
    ? []
    : [
        buildOrganizationStructuredData(),
        buildLocalBusinessStructuredData(),
        buildWebSiteStructuredData(),
        buildWebPageStructuredData({
          path: normalizedPath,
          title,
          description,
        }),
        buildBreadcrumbListStructuredData(normalizedPath, breadcrumbTitle),
      ].filter(Boolean);

  const allStructuredData = [...defaultStructuredData, ...pageStructuredData];
  const structuredDataEntries = allStructuredData.filter((entry, index) => {
    const id = entry?.["@id"];
    if (!id) {
      return true;
    }

    return (
      allStructuredData.findIndex((candidate) => candidate?.["@id"] === id) === index
    );
  });

  return (
    <Helmet>
      <html lang="pl" />
      <title>{title}</title>
      {description ? <meta name="description" content={description} /> : null}
      <meta name="robots" content={robotsContent} />
      {shouldExposeCanonical ? <link rel="canonical" href={canonicalUrl} /> : null}
      {shouldExposeCanonical ? (
        <link rel="alternate" hrefLang="pl-PL" href={canonicalUrl} />
      ) : null}
      {shouldExposeCanonical ? (
        <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
      ) : null}

      <meta property="og:locale" content={SITE_LOCALE} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={title} />
      {description ? <meta property="og:description" content={description} /> : null}
      {shouldExposeCanonical ? <meta property="og:url" content={canonicalUrl} /> : null}
      <meta property="og:image" content={toAbsoluteUrl(ogImage)} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      {description ? <meta name="twitter:description" content={description} /> : null}
      <meta name="twitter:image" content={toAbsoluteUrl(ogImage)} />

      {structuredDataEntries.map((entry, index) => (
        <script key={`structured-data-${index}`} type="application/ld+json">
          {JSON.stringify(entry)}
        </script>
      ))}
    </Helmet>
  );
}
